import React from 'react';
import './AdministrativeBreadcrumb.css';

const PinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 22s7-6.1 7-12a7 7 0 1 0-14 0c0 5.9 7 12 7 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.6" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const LevelLabel = ({ level, className, truncate }) => (
  <span
    className={[
      'administrative-breadcrumb-label',
      truncate
        ? 'administrative-breadcrumb-label-truncate'
        : 'administrative-breadcrumb-label-full',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    title={level.label}
    onClick={level.onClick}
    onKeyDown={(event) => {
      if ((event.key === 'Enter' || event.key === ' ') && level.onClick) {
        event.preventDefault();
        level.onClick();
      }
    }}
    role={level.onClick ? 'button' : undefined}
    tabIndex={level.onClick ? 0 : undefined}
  >
    {level.label}
  </span>
);

const AdministrativeBreadcrumb = ({
  show,
  showHeader,
  maxWidth,
  compactTrail = false,
  /** Reserva extra bajo el header (barra de búsqueda mobile). */
  topOffset = 0,
  selectedProvinceName,
  selectedDepartmentName,
  selectedPedaniaName,
  selectedMunicipalityName,
  onClear,
  onProvinceClick,
  onDepartmentClick,
  onPedaniaClick,
  onMunicipalityClick,
}) => {
  if (!show) return null;

  const levels = [
    {
      key: 'province',
      label: selectedProvinceName,
      onClick: onProvinceClick,
    },
    {
      key: 'department',
      label: selectedDepartmentName,
      onClick: onDepartmentClick,
    },
    {
      key: 'pedania',
      label: selectedPedaniaName,
      onClick: onPedaniaClick,
    },
    {
      key: 'municipality',
      label: selectedMunicipalityName,
      onClick: onMunicipalityClick,
    },
  ].filter((level) => Boolean(level.label));

  if (levels.length === 0) return null;

  const currentLevel = levels[levels.length - 1];
  const canExpand = levels.length > 1;

  const breadcrumbStyle = {
    top: showHeader ? `${88 + Math.max(0, Number(topOffset) || 0)}px` : '16px',
    ...(maxWidth ? { '--breadcrumb-max-width': maxWidth } : {}),
  };

  return (
    <div
      className={[
        'administrative-breadcrumb',
        canExpand ? 'administrative-breadcrumb-expandable' : '',
        compactTrail ? 'administrative-breadcrumb-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={breadcrumbStyle}
    >
      <div className="administrative-breadcrumb-primary">
        <span className="administrative-breadcrumb-pin">
          <PinIcon />
        </span>

        <div className="administrative-breadcrumb-current">
          <LevelLabel level={currentLevel} truncate />
        </div>

        <button
          type="button"
          onClick={onClear}
          aria-label="Quitar selección administrativa"
          title="Quitar selección"
          className="administrative-breadcrumb-close"
        >
          <CloseIcon />
        </button>
      </div>

      {canExpand && (
        <div className="administrative-breadcrumb-hierarchy">
          <div className="administrative-breadcrumb-hierarchy-inner">
            <div className="administrative-breadcrumb-divider" />
            <div className="administrative-breadcrumb-trail">
              {levels.map((level, index) => (
                <React.Fragment key={level.key}>
                  {index > 0 && (
                    <span className="administrative-breadcrumb-separator">&gt;</span>
                  )}
                  <LevelLabel
                    level={level}
                    truncate={compactTrail ? index > 0 : index > 0 && levels.length > 3}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdministrativeBreadcrumb;
